import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  Dispatch,
  Fragment,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SuccessAlertContext } from "App";
import Notify from "config/notify";
import { OrderCheck, OrderSupplierCsv } from "../types";
import TableCellSmall, {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "./TableCellSmall";
import { localeStringDisZero } from "../utils/common";
import { INT_MAX } from "../constants";
import Mask from "./Mask";

const limit = 10;

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    // textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    // textAlign: "center",
    p: 0.5,
    top: 57,
  },
  supplierUnderBottomCol: {
    // textAlign: "center",
    p: 0.5,
    top: 91,
  },
};

type Props = {
  open: boolean;
  title: string;
  content: string;
  ActionButtons: JSX.Element;
  onClose: Function;
  orderList: OrderSupplierCsv[];
  editedOrderList?: OrderSupplierCsv[];
  setEditedOrderList?: Dispatch<SetStateAction<OrderSupplierCsv[]>>;
  isFastLoading?: MutableRefObject<boolean>;
  getMoreStockList?: Function;
  setInit?: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
  isMoreLoading?: boolean;
  error?: string;
  errorTitle?: string;
  editable?: boolean;
};
/**
 * 出荷CSV確認、出荷確認モーダルのテーブル
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function OrderSupplierConfirmTable({
  open,
  title,
  content,
  ActionButtons,
  onClose,
  orderList,
  editedOrderList,
  setEditedOrderList,
  isFastLoading,
  getMoreStockList,
  setInit,
  isLoading,
  isMoreLoading,
  error,
  errorTitle,
  editable,
}: Props) {
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false);
  const [adjustment, setAdjustment] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const innerRef = useRef<HTMLDivElement | undefined>(undefined);
  const { showAlert } = useContext(SuccessAlertContext);

  // スクロール処理
  const handleScroll = async () => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading?.current || !contentRef.current) {
      return;
    }
    const scrollTop = contentRef.current?.scrollTop;
    const contentHeight = contentRef.current?.clientHeight;
    const scrollHeight = contentRef.current?.scrollHeight;
    if (scrollTop + contentHeight + 10 >= scrollHeight) {
      // eslint-disable-next-line no-param-reassign
      if (isFastLoading) isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      if (getMoreStockList) await getMoreStockList();
    })();
  }, [fireMoreLoadEvent]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (setInit) {
      setInit(true);
      // if (error) showAlert(error, "error");
      if (error) {
        Notify?.setNotification?.({
          show: true,
          title: "CSVアップロードでエラーがあります。",
          message: error,
        });
      }
    }
  }, [open]);
  useEffect(() => {
    if (setEditedOrderList) {
      // console.log('structuredClone orderList:', orderList, editedOrderList)
      setEditedOrderList(structuredClone(orderList));
    }
  }, [JSON.stringify(orderList)]);

  // 表示数がlimitの時、中のコンテンツが小さいと無限ローディングさせることができないため、少しスクロールできるようにする。
  useEffect(() => {
    if (orderList.length === limit && !isMoreLoading) {
      // console.log('adjustment', adjustment, orderList.length, contentRef.current?.clientHeight);
      setTimeout(() => {
        if (
          contentRef.current &&
          innerRef.current &&
          contentRef.current.clientHeight - innerRef.current.clientHeight >= 0
        ) {
          // 中のコンテンツが小さい場合、調整オブジェクトを挿入
          setAdjustment(contentRef.current.clientHeight - innerRef.current.clientHeight + 1);
        } else {
          setAdjustment(undefined);
        }
      }, 0);
    } else {
      setAdjustment(undefined);
    }
  }, [orderList, isMoreLoading]);
  return (
    <Dialog open={open} onClose={() => onClose()} fullScreen>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} onScroll={handleScroll}>
        <Box ref={innerRef}>
          <DialogContentText>{error ? errorTitle : content}</DialogContentText>
          <TableContainer
            component={Paper}
            sx={{ overflowX: "initial", width: "fit-content", minWidth: "100%" }}
          >
            <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="order table">
              {((editable && editedOrderList) ? editedOrderList : orderList).length !== 0 && (
                <>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        ステータス
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        注文番号
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        商品コード
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        JANコード
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        ブランド
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        商品名
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        サイズ
                      </TableHeaderCellNarrow>
                      <TableHeaderCellWide isTwoHeight sx={styles.supplierTopCol}>品番</TableHeaderCellWide>
                      <TableHeaderCellWide isTwoHeight right sx={styles.supplierTopCol}>
                        受注数
                      </TableHeaderCellWide>
                      <TableHeaderCellNarrow isTwoHeight right sx={styles.supplierTopCol}>
                        金額
                        {/* <br />
                        (単価) */}
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        注文日
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        納品希望日
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        仕入先
                        <br />
                        出荷日
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        仕入先
                        <br />
                        出荷予定日
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow isTwoHeight sx={styles.supplierTopCol}>
                        仕入先
                        <br />
                        入荷予定
                      </TableHeaderCellNarrow>
                      {error && <TableHeaderCellSmall rowSpan={3}>エラー内容</TableHeaderCellSmall>}
                    </TableRow>
                    <TableRow>
                      <TableHeaderCellWide center sx={styles.supplierBottomCol} colSpan={8}>
                        発送先情報
                      </TableHeaderCellWide>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol} rowSpan={2}>
                        配送社名
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol} rowSpan={2}>
                        送り状番号
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierBottomCol} rowSpan={2}>
                        出荷数
                      </TableHeaderCellNarrow>
                      <TableHeaderCellWide sx={styles.supplierBottomCol} rowSpan={2} colSpan={4}>
                        備考
                      </TableHeaderCellWide>
                    </TableRow>
                    <TableRow>
                      <TableHeaderCellNarrow sx={styles.supplierUnderBottomCol}>
                        郵便番号
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierUnderBottomCol} colSpan={3}>
                        住所
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierUnderBottomCol}>
                        発送先名
                      </TableHeaderCellNarrow>
                      <TableHeaderCellNarrow sx={styles.supplierUnderBottomCol} colSpan={3}>
                        電話番号
                      </TableHeaderCellNarrow>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {((editable && editedOrderList) ? editedOrderList : orderList).map((order, index) => {
                      const isZebraStripeRow = !(error && order.error) ? (index % 2 === 1) : false;
                      return (
                      <>
                        <TableRow
                          key={order.productCd}
                          sx={error && order.error ? { backgroundColor: "var(--red-400)" } : {}}
                        >
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.orderStatus}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.orderNumber}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.productCd}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.janCd}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.brand}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.productName}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.size}</TableCellNarrow>
                          <TableCellWide multi gray={isZebraStripeRow}>{order.productNumber}</TableCellWide>
                          {/* { editable && setEditedOrderList
                            ? <TableCellWide center gray={isZebraStripeRow}>
                                <TextField type="Number" size="small" value={localeStringDisZero(order.orderCount)} onChange={
                                    (e) => {
                                      if (Number(e.target.value) > INT_MAX) {
                                        return;
                                      }
                                      setEditedOrderList(prev => {
                                        const clonedList = structuredClone(prev);
                                        clonedList[index].orderCount = Number(e.target.value);
                                        return clonedList;
                                      })
                                    }
                                  }
                                  inputProps={{
                                    sx: {fontSize: 12, textAlign: 'right'},
                                    min: 0,
                                    max: INT_MAX
                                  }}
                                  onKeyDown={(e) => {if (e.key === '-' || e.key === '.') e.preventDefault()}}
                                />
                              </TableCellWide>
                            : <TableCellWide right multi gray={isZebraStripeRow}>{localeStringDisZero(order.orderCount)}</TableCellWide>
                          } */}
                          <TableCellWide right multi gray={isZebraStripeRow}>{localeStringDisZero(order.orderCount)}</TableCellWide>
                          <TableCellNarrow multi gray={isZebraStripeRow}>
                            {/* {localeStringDisZero(order.headquartersCost)}<br /> */}
                          {`${localeStringDisZero(order.headquartersUnitCost)}`}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.orderDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.deliveryDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.shippingDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.expectedShippingDate}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.arrivalDate}</TableCellNarrow>
                          {error &&
                            (order.error ? (
                              <TableCellWide rowSpan={2}>
                                <MultiTypography><>{
                                  // eslint-disable-next-line react/no-array-index-key
                                  order.error.split('\n').map((e,i) => (i === 0 ? e : <Fragment key={i}><br/>{e}</Fragment>))
                                }</></MultiTypography>
                              </TableCellWide>
                            ) : (
                              <TableCellWide rowSpan={2} />
                            ))
                          }
                        </TableRow>
                        <TableRow
                          sx={(error && order.error) ? { backgroundColor: "var(--red-400)" } : {}}
                        >
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.postalCd}</TableCellNarrow>
                          <TableCellWide colSpan={3} multi gray={isZebraStripeRow}>{order.destinationAddress}</TableCellWide>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.destinationName}</TableCellNarrow>
                          <TableCellNarrow colSpan={3} multi gray={isZebraStripeRow}>{order.phoneNumber}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.shippingCompanyName}</TableCellNarrow>
                          <TableCellNarrow multi gray={isZebraStripeRow}>{order.trackingNumber}</TableCellNarrow>
                          { editable && setEditedOrderList
                            ? <TableCellWide center gray={isZebraStripeRow}>
                                <TextField type="Number" size="small" value={order.shippingCount} onChange={
                                    (e) => {
                                      if (Number(e.target.value) > INT_MAX) {
                                        return;
                                      }
                                      setEditedOrderList(prev => {
                                        const clonedList = structuredClone(prev);
                                        clonedList[index].shippingCount = Number(e.target.value);
                                        return clonedList;
                                      })
                                    }
                                  }
                                  inputProps={{
                                    sx: {fontSize: 12, textAlign: 'right'},
                                    min: 0,
                                    max: INT_MAX
                                  }}
                                  onKeyDown={(e) => {if (e.key === '-' || e.key === '.') e.preventDefault()}}
                                />
                              </TableCellWide>
                            : <TableCellWide right multi gray={isZebraStripeRow}>{localeStringDisZero(order.shippingCount)}</TableCellWide>
                          }
                          <TableCellWide colSpan={4} multi gray={isZebraStripeRow}>{order.shopRemarks}</TableCellWide>
                        </TableRow>
                      </>
                    )})}
                  </TableBody>
                </>
              )}
            </Table>
          </TableContainer>
          {adjustment && ( // 画面が大きいと無限スクロールが発火しないことがある
            <Box height={adjustment} />
          )}
          {isMoreLoading && (
            <Box py={3} textAlign="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>{ActionButtons}</DialogActions>
      <Mask isLoading={isLoading} />
    </Dialog>
  );
}
